import { ReactNode } from 'react';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { EntityPredicate } from './entityPredicates';

/**
 * Renders its children only when the predicate holds for the current entity
 * (feature 0031).
 *
 * Wrap the card's `<Grid item>` **inside** `When`, not around it, so that a hidden
 * card emits no grid slot at all — an empty `<Grid item>` still consumes its
 * columns and the container's spacing, which is what leaves holes in an overview
 * row. This is the same reason `SystemAwsAccountsCard` owns its own `<Grid item>`.
 *
 * ```tsx
 * <When is={hasLinks}>
 *   <Grid item md={4} xs={12}>
 *     <EntityLinksCard />
 *   </Grid>
 * </When>
 * ```
 *
 * Deliberately used in preference to `<EntitySwitch><EntitySwitch.Case if={…}>`
 * for visibility: `EntitySwitch` is first-match-wins, so it needs one wrapper per
 * card, and a `Case` with no `if` silently becomes the switch's default — which is
 * how this file previously ended up with a guard that always matched.
 * `EntitySwitch` remains the right tool for kind/type *dispatch*.
 */
export const When = (props: { is: EntityPredicate; children: ReactNode }) => {
  const { entity } = useAsyncEntity();

  // Nothing is rendered until the entity resolves. Predicates are synchronous, so
  // this is the entity page's own load, not a per-card fetch — every guarded card
  // appears together on first paint rather than popping in one at a time.
  if (!entity || !props.is(entity)) {
    return null;
  }

  return <>{props.children}</>;
};
